// Daily-View "Fill tickets" worklist: one row per order × SKU for the current
// view, showing whether an OPEN fill ticket already covers it (✓ #ticket) or it
// still needs one (✗ MISSING). Critical-PO lines are flagged and sorted first,
// then by size (lbs). Pass the already-filtered view rows so it mirrors the
// priority/coverage controls.

import { useMemo } from 'react';
import type { OpenFillTicketMap } from '../../../services/cplex';
import { coverageOf, itemCodeOf } from './dailyFilters';
import { stripCode } from './mppHelpers';
import type { MPPRow } from './types';
import styles from './DailyDashboard.module.css';
import ft from './FillTicketPanel.module.css';

interface WorklistRow {
  order: string;
  sku: string;
  lbs: number;
  critical: boolean;
  covered: boolean;
  ticket: number | null;
}

const fmtInt = (n: number) => Math.round(n).toLocaleString();

export function FillTicketPanel({
  rows,
  tickets,
  fill = false,
}: {
  rows: readonly MPPRow[];
  tickets: OpenFillTicketMap | null | undefined;
  fill?: boolean;
}) {
  const data = useMemo<WorklistRow[]>(() => {
    const agg = new Map<string, WorklistRow>();
    for (const r of rows) {
      if (r.filled_from === 'Dropped / Unfilled') continue;
      const code = itemCodeOf(r.sku);
      if (!code) continue;
      const key = `${r.order ?? ''}|${code}`;
      const lbs = Number(r.s_to_o) || 0;
      const existing = agg.get(key);
      if (existing) {
        existing.lbs += lbs;
        if (r.critical_po === 'Yes') existing.critical = true;
        continue;
      }
      const cov = coverageOf(r, tickets);
      agg.set(key, {
        order: r.order ?? '',
        sku: stripCode(r.sku ?? ''),
        lbs,
        critical: r.critical_po === 'Yes',
        covered: cov != null,
        ticket: cov?.tickets[0]?.ticket ?? null,
      });
    }
    return Array.from(agg.values()).sort(
      (a, b) =>
        Number(b.critical) - Number(a.critical) || b.lbs - a.lbs,
    );
  }, [rows, tickets]);

  const missing = useMemo(() => data.filter((r) => !r.covered).length, [data]);

  return (
    <div className={`${styles.card} ${fill ? styles.cardFill : ''}`}>
      <div className={styles.cardHd}>
        <div>
          <div className={styles.cardTitle}>◷ Fill tickets</div>
          <div className={styles.cardSub}>
            Order × SKU — critical first, then by size. ✓ covered · ✗ to create
          </div>
        </div>
        <div className={styles.cardHdActions}>
          <span className={`${styles.pill} ${styles.pillBad}`}>{missing} missing</span>
        </div>
      </div>
      <div className={styles.scrollArea}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th style={{ width: 28 }} title="Critical PO">!</th>
              <th>Order</th>
              <th>SKU</th>
              <th className={ft.num}>Lbs</th>
              <th>Fill ticket</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={i} className={r.covered ? '' : ft.missingRow}>
                <td style={{ textAlign: 'center' }}>
                  {r.critical ? <span className={ft.crit} title="Critical PO">●</span> : ''}
                </td>
                <td title={r.order}>{r.order}</td>
                <td title={r.sku}>{r.sku}</td>
                <td className={ft.num}>{fmtInt(r.lbs)}</td>
                <td>
                  {r.covered ? (
                    <span className={ft.covered}>✓ #{r.ticket}</span>
                  ) : (
                    <span className={ft.missing}>✗ MISSING</span>
                  )}
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={5} className={ft.empty}>
                  No order lines in the current view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
